import type {
  ProviderAvailability,
  ProviderKind,
  ProviderUnavailableDetail,
  ProviderUnavailableReason,
  ServerProvider,
} from "@cmmd-forge/contracts";

import { classifyEnvironmentId } from "../../environments/environmentIdClassification";

/**
 * Reconstitute a picker-usable inventory from the control plane's last-known
 * provider catalog.
 *
 * WHY THIS EXISTS
 *
 * `/new` for a cloud project whose sprite is stopped had no inventory to show:
 * only the target runtime knows what it can execute, and the resolver
 * deliberately refuses to connect a stopped runtime (waking one to populate a
 * dropdown would cost a sprite start per page view). The picker therefore said
 * "Models load when this project starts. Send to start it." — which asks the
 * user to send a turn blind in order to discover which model they could have
 * picked for it. Choosing a model required starting the workspace; starting the
 * workspace was the thing they were trying to configure.
 *
 * The control plane already has the answer. Every runtime publishes its
 * Provider Inventory into the runtime inventory, and that record survives the
 * sprite that produced it. It stores precisely what a picker renders — provider
 * kind, model slug, model name — so the cycle breaks without waking anything.
 *
 * WHY SYNTHESIZING A `ServerProvider` HERE IS SOUND
 *
 * The stored catalog omits `status`, `installed`, `enabled` and auth, for size.
 * Since FOR-36364 it carries an explicit `availability` instead, so this
 * restates what the Environment actually said rather than inferring readiness
 * from presence in the list.
 *
 * An entry with NO availability came from a publisher that predates FOR-36364,
 * and that publisher filtered to executable providers before serializing.
 * Presence was its assertion of executability, so absence here is read as
 * available. That is the one deliberate absence-to-state mapping in this
 * program, and it is earned by the old filter rather than assumed; every other
 * consumer reads the field.
 *
 * The one thing it must NOT restate is auth. Credential provenance is per-user
 * and the inventory row is shared CMMD-side state, so `auth` is excluded at the
 * boundary on purpose. We genuinely do not know whether a stopped runtime still
 * holds a credential, and `unknown` says exactly that — it clears the
 * executability gate (which excludes only an explicit `unauthenticated`)
 * without claiming a credential we cannot see.
 */

/** The control plane's stored shape — mirrors CMMD's provider catalog schema. */
export interface LastKnownProviderEntry {
  readonly provider: string;
  readonly models: ReadonlyArray<{
    readonly slug: string;
    readonly name: string;
    readonly isCustom?: boolean;
  }>;
  readonly availability?: string | undefined;
  readonly unavailableReason?: string | undefined;
  readonly unavailableDetail?: string | undefined;
  readonly version?: string | null | undefined;
  readonly binaryPath?: string | null | undefined;
}

const UNAVAILABLE_REASONS: ReadonlySet<string> = new Set<ProviderUnavailableReason>([
  "unbuilt",
  "not-offered",
  "not-usable",
  "not-on-this-environment",
]);

const UNAVAILABLE_DETAILS: ReadonlySet<string> = new Set<ProviderUnavailableDetail>([
  "no-connection",
  "delivery-pending",
  "reconnect-required",
]);

const KNOWN_PROVIDER_KINDS: ReadonlySet<string> = new Set<ProviderKind>([
  "codex",
  "claudeAgent",
  "ollama",
  "cmmd",
  "apex",
  "cursor",
  "opencode",
]);

/**
 * What the stored entry says about availability, in the contract's vocabulary.
 *
 * Only the reasons this build's copy covers are carried through. An entry
 * naming one it does not know is still withheld, just without a sentence, which
 * beats rendering a raw enum member at a person.
 */
function storedAvailability(entry: LastKnownProviderEntry): {
  readonly availability: ProviderAvailability;
  readonly unavailableReason?: ProviderUnavailableReason;
  readonly unavailableDetail?: ProviderUnavailableDetail;
} {
  if (entry.availability !== "unavailable") return { availability: "available" };
  const reason = entry.unavailableReason;
  const detail = entry.unavailableDetail;
  return {
    availability: "unavailable",
    ...(reason !== undefined && UNAVAILABLE_REASONS.has(reason)
      ? { unavailableReason: reason as ProviderUnavailableReason }
      : {}),
    ...(detail !== undefined && UNAVAILABLE_DETAILS.has(detail)
      ? { unavailableDetail: detail as ProviderUnavailableDetail }
      : {}),
  };
}

function reconstituteProvider(
  entry: LastKnownProviderEntry,
  publishedAt: string,
): ServerProvider | null {
  // An older control plane may know a provider kind this build does not. Drop
  // it rather than rendering an entry the picker cannot route a turn to.
  if (!KNOWN_PROVIDER_KINDS.has(entry.provider)) return null;
  const isWithheld = entry.availability === "unavailable";
  // `models.length > 0` is part of the executability gate, so an AVAILABLE
  // provider that published none can never be selectable — omit it instead of
  // listing a provider whose model list is empty. A Withheld Provider is kept
  // either way: it is expected to have no models, and dropping it would put the
  // picker back to reading a gap in the list as a state.
  if (!isWithheld && entry.models.length === 0) return null;

  return {
    provider: entry.provider as ProviderKind,
    ...storedAvailability(entry),
    // The three legacy booleans cannot express four reasons — that is why
    // availability exists. For a Withheld Provider they take the value that
    // keeps `isServerProviderExecutable` false, so no turn can be dispatched to
    // a provider the Environment said it will not run; `unavailableReason`
    // carries the truth a person reads.
    enabled: !isWithheld,
    installed: !isWithheld,
    status: isWithheld ? "disabled" : "ready",
    version: entry.version ?? null,
    // Not `authenticated`: see the auth note above. `unknown` is the honest
    // answer for a runtime that is not currently running.
    auth: { status: "unknown" },
    // When the runtime last told us — deliberately NOT now. Stamping this with
    // the current time would present a stale catalog as freshly verified.
    checkedAt: publishedAt,
    models: entry.models.map((model) => ({
      slug: model.slug,
      name: model.name,
      isCustom: model.isCustom ?? false,
      // The control plane does not store capabilities — Forge resolves those
      // from its own built-in catalogs, which is why they were left out of the
      // published catalog for size. `null` says "not known from this source"
      // rather than asserting a capability set we did not receive.
      capabilities: null,
    })),
    slashCommands: [],
    skills: [],
  };
}

/**
 * One runtime inventory row, structurally. Deliberately narrower than the
 * generated `CmmdRuntimeInventoryRuntime` so this stays testable without
 * constructing the full response type, and so a codegen refresh that adds
 * fields cannot break it.
 */
export interface LastKnownInventoryRuntime {
  readonly runtime: { readonly id: string };
  readonly inventoryPublishedAt: string | null;
  readonly providers?: ReadonlyArray<LastKnownProviderEntry> | undefined;
}

/**
 * The lookup both `selectLastKnownProvidersForEnvironment` and
 * `selectLastKnownInventoryPublishedAt` share: which stored runtime row, if
 * any, answers for this environment id. Kept as one function so the two
 * callers can never resolve to a different row for the same input.
 */
function findLastKnownInventoryEntry(input: {
  readonly targetEnvironmentId: string | null;
  readonly runtimeId?: string | null;
  readonly runtimes: ReadonlyArray<LastKnownInventoryRuntime>;
}): LastKnownInventoryRuntime | null {
  if (input.targetEnvironmentId === null) return null;
  const runtimeId =
    input.runtimeId ?? classifyEnvironmentId(input.targetEnvironmentId).legacyRuntimeId;
  if (runtimeId === null) return null;
  // The control plane has never heard of the runtime: unknown, not empty.
  return input.runtimes.find((candidate) => candidate.runtime.id === runtimeId) ?? null;
}

/**
 * The join: environment id -> runtime inventory row -> stored catalog.
 *
 * `cmmd-runtime:<id>` addresses a runtime that is or was provisioned, and is
 * the only shape that can ever have published a catalog through this lookup.
 * `cmmd-cloud-project:<id>` is a registered project with no VM behind it. It
 * lives in its own namespace precisely because there is nothing to address.
 * CMMD's durable `env_…` id has no encoded runtime id either, so without an
 * explicit `runtimeId` it resolves the same way: `null`, keeping the "not
 * started yet" copy rather than borrowing another runtime's models.
 */
export function selectLastKnownProvidersForEnvironment(input: {
  readonly targetEnvironmentId: string | null;
  readonly runtimeId?: string | null;
  readonly runtimes: ReadonlyArray<LastKnownInventoryRuntime>;
}): ReadonlyArray<ServerProvider> | null {
  const entry = findLastKnownInventoryEntry(input);
  if (!entry) return null;
  // A catalog with no publish time is internally inconsistent — both are
  // written together — and `checkedAt` is required. Fail closed rather than
  // fabricate a timestamp, for the same reason we never stamp it with `now`.
  if (entry.inventoryPublishedAt === null) return null;

  return providersFromLastKnownCatalog({
    catalog: entry.providers,
    publishedAt: entry.inventoryPublishedAt,
  });
}

/**
 * The publish time behind `selectLastKnownProvidersForEnvironment`'s answer,
 * so the picker can say how old a last-known catalog is without reading it
 * back off `checkedAt`. Every reconstituted provider's `checkedAt` already
 * equals this value, but treating that as the staleness signal would make
 * "how old" a property of the DATA rather than of the PATH that produced it —
 * indistinguishable from a live check that merely ran a while ago.
 *
 * Non-null under exactly the same condition
 * `selectLastKnownProvidersForEnvironment` returns non-null: a matched row,
 * with a publish time, whose `providers` field is present (possibly empty).
 */
export function selectLastKnownInventoryPublishedAt(input: {
  readonly targetEnvironmentId: string | null;
  readonly runtimeId?: string | null;
  readonly runtimes: ReadonlyArray<LastKnownInventoryRuntime>;
}): string | null {
  const entry = findLastKnownInventoryEntry(input);
  if (!entry || entry.inventoryPublishedAt === null || entry.providers === undefined) return null;
  return entry.inventoryPublishedAt;
}

export function providersFromLastKnownCatalog(input: {
  readonly catalog: ReadonlyArray<LastKnownProviderEntry> | undefined;
  readonly publishedAt: string;
}): ReadonlyArray<ServerProvider> | null {
  // Absent and empty are different answers and stay that way end to end:
  // absent means the runtime's image predates the field (unknown), `[]` means
  // it answered and can execute nothing. The picker renders a different
  // sentence for each, so collapsing them here would reintroduce the original
  // bug one layer down.
  if (input.catalog === undefined) return null;

  const providers: ServerProvider[] = [];
  for (const entry of input.catalog) {
    const provider = reconstituteProvider(entry, input.publishedAt);
    if (provider) providers.push(provider);
  }
  return providers;
}
